import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import ApprovalItemsStatus from '@/components/Approval/@components/ApprovalItemsStatus';
import {
  countPurchaseRequisitionCards,
  PurchaseRequisitionSnapshot,
  type PurchaseRequisitionCardRef,
} from '@/components/Approval/@components/PurchaseRequisition';
import usePartialReject, {
  getPartialRejectErrorMessage,
} from '@/hooks/approval/usePartialReject';
import useApprovalView from '@/hooks/approval/useApprovalView';
import { useSnackbar } from '@/hooks/global-tools';
import { MyInfoState } from '@/recoil/atom';
import {
  parsePurchaseRequisitionContent,
  PURCHASE_REQUISITION_REJECT_REASON_MAX_LENGTH,
} from '@/utils/approval/purchaseRequisition';
import { isCurrentApproverMeOf } from '../../actionVisibility';
import ReasonInputModal from '../../ReasonInputModal';
import type { ApprovalViewResponse, ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

/** 부분반려 버튼: 진행중 + 지금 내 결재 차례 + 카드 2장 이상(레거시 조건) */
export const canPartialRejectPurchaseRequisition = (
  view: ApprovalViewResponse | undefined,
  myPassportId: string,
  content: string | null,
) =>
  !!view &&
  view.status === 'P' &&
  isCurrentApproverMeOf(
    view.approvers,
    view.currentApproverOrder,
    myPassportId,
    view.status,
  ) &&
  countPurchaseRequisitionCards(content) > 1;

/**
 * 122 GERP PR(제상품) — 저장 본문 그대로(레거시 문서 포함). GERP 재조회 안 함.
 */
const PurchaseRequisitionSlot = ({ data }: ContentExtraProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const myInfo = useAtomValue(MyInfoState);
  const { data: view } = useApprovalView(data.documentId);
  const { partialReject, submitting } = usePartialReject(data.documentId);
  const content = parsePurchaseRequisitionContent(data.attributes);

  const canPartialReject = useMemo(
    () =>
      canPartialRejectPurchaseRequisition(
        view,
        myInfo?.passportId ?? '',
        content,
      ),
    [view, myInfo?.passportId, content],
  );

  // 닫히는 동안 제목이 비지 않게 대상은 남겨 둠
  const [target, setTarget] = useState<PurchaseRequisitionCardRef | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderCardAction = useCallback(
    (card: PurchaseRequisitionCardRef) =>
      canPartialReject && card.aprvId ? (
        <BoxButton
          type="button"
          variant="secondary"
          size="small"
          onClick={() => {
            setTarget(card);
            setIsModalOpen(true);
          }}
        >
          {t('부분반려')}
        </BoxButton>
      ) : null,
    [canPartialReject, t],
  );

  // 레거시 122 는 확인창 없이 바로 처리
  const handleSubmit = async (reason: string) => {
    if (!target || submitting) return;
    try {
      await partialReject(target.aprvId, reason);
      setIsModalOpen(false);
      showSuccess({ message: t('부분 반려가 완료 되었습니다.') });
    } catch (error) {
      showError({ message: t(getPartialRejectErrorMessage(error)) });
    }
  };

  if (!content) return null;
  return (
    <ContentSlotRoot>
      <ApprovalItemsStatus documentId={data.documentId} />
      <PurchaseRequisitionSnapshot
        content={content}
        renderCardAction={renderCardAction}
      />
      <ReasonInputModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
        title={t('{{styleNo}} {{cardNo}}차 반려', {
          styleNo: target?.styleNo ?? '',
          cardNo: target?.cardNo ?? '',
        })}
        submitLabel={t('반려처리')}
        reasonLabel={t('사유입력')}
        reasonPlaceholder={t('사유를 입력해주세요.')}
        reasonMaxLength={PURCHASE_REQUISITION_REJECT_REASON_MAX_LENGTH}
      />
    </ContentSlotRoot>
  );
};

export default PurchaseRequisitionSlot;
